import React, { useState, useEffect } from 'react';
import CategoryInput from './CategoryInput';
import DatePicker from './DatePicker';
import { generateUploadKey } from '../utils/uploadKeyUtils';

const EditAlbumModal = ({ isOpen, onClose, onUpdateAlbum, albumData }) => {
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [category, setCategory] = useState('');
  const [isRestricted, setIsRestricted] = useState(false);
  const [allowedEmails, setAllowedEmails] = useState('');

  useEffect(() => {
    if (albumData && isOpen) {
      setTitle(albumData.title || '');
      setDate(albumData.date ? albumData.date.split('T')[0] : '');
      setCategory(albumData.category || '');
      setIsRestricted(albumData.isRestricted || false);
      // Convert array to comma-separated string
      setAllowedEmails(albumData.allowedEmails?.join(', ') || '');
    }
  }, [albumData, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (title.trim()) {
      // Parse comma-separated emails and clean them up
      const emailList = isRestricted
        ? allowedEmails.split(',').map(email => email.trim().toLowerCase()).filter(email => email)
        : [];

      // Generate upload key if missing for existing albums
      const uploadKey = albumData.uploadKey || generateUploadKey();

      onUpdateAlbum({
        ...albumData,
        title: title.trim(),
        date,
        category: category.trim(),
        isRestricted,
        allowedEmails: emailList,
        uploadKey,
        updatedAt: new Date().toISOString()
      });
      onClose();
    }
  };

  const handleRegenerateUploadKey = () => {
    if (window.confirm('Regenerate upload link? This will invalidate the old link.')) {
      const newUploadKey = generateUploadKey();
      onUpdateAlbum({
        ...albumData,
        uploadKey: newUploadKey,
        updatedAt: new Date().toISOString()
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]" onClick={onClose}>
      <div className="bg-primary border border-gray-200 p-8 rounded-lg w-[90%] max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="m-0 mb-6 text-text-dark font-serif">Edit Album</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="edit-title" className="block mb-2 text-text-dark font-medium">Title:</label>
            <input
              type="text"
              id="edit-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              className="w-full p-3 border border-text-dark/30 rounded text-base box-border focus:outline-none focus:border-accent focus:shadow-[0_0_0_2px_rgba(186,230,253,0.25)]"
            />
          </div>
          <div className="mb-4">
            <label htmlFor="edit-date" className="block mb-2 text-text-dark font-medium">Date:</label>
            <DatePicker
              id="edit-date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </div>
          <div className="mb-4">
            <label htmlFor="edit-category" className="block mb-2 text-text-dark font-medium">Category:</label>
            <CategoryInput
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            />
          </div>
          <div className="mb-4 border-t border-gray-200 pt-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isRestricted}
                onChange={(e) => setIsRestricted(e.target.checked)}
                className="w-4 h-4 cursor-pointer"
              />
              <span className="text-text-dark font-medium">Restrict Access</span>
            </label>
            <p className="text-sm text-gray-600 mt-1 ml-6">
              Only specified users can view this album
            </p>
          </div>
          {isRestricted && (
            <div className="mb-4">
              <label htmlFor="edit-allowedEmails" className="block mb-2 text-text-dark font-medium">
                Allowed Emails:
              </label>
              <textarea
                id="edit-allowedEmails"
                value={allowedEmails}
                onChange={(e) => setAllowedEmails(e.target.value)}
                placeholder="user1@example.com, user2@example.com"
                rows={3}
                className="w-full p-3 border border-text-dark/30 rounded text-base box-border focus:outline-none focus:border-accent focus:shadow-[0_0_0_2px_rgba(186,230,253,0.25)] resize-vertical"
              />
              <p className="text-sm text-gray-600 mt-1">
                Enter email addresses separated by commas
              </p>
            </div>
          )}
          <div className="mb-4 border-t border-gray-200 pt-4">
            <label className="block mb-2 text-text-dark font-medium">Anonymous Upload Link:</label>
            <p className="text-sm text-gray-600 mb-3">
              Share this link to allow others to upload photos without authentication
            </p>
            <button
              type="button"
              onClick={handleRegenerateUploadKey}
              className="px-4 py-2 border border-orange-500 bg-orange-50 text-orange-700 rounded text-sm cursor-pointer transition-colors duration-200 flex items-center gap-2 hover:bg-orange-100"
            >
              <span className="material-icons text-base">refresh</span>
              Regenerate Upload Link
            </button>
          </div>
          <div className="flex gap-4 justify-end mt-6">
            <button type="button" onClick={onClose} className="px-6 py-3 border border-gray-300 rounded text-base cursor-pointer transition-colors duration-200 flex items-center gap-2 bg-primary text-text-dark hover:bg-gray-50">
              <span className="material-icons">close</span>
              Cancel
            </button>
            <button type="submit" className="px-6 py-3 border-none rounded text-base cursor-pointer transition-colors duration-200 flex items-center gap-2 bg-accent text-text-light hover:bg-accent/80">
              <span className="material-icons">save</span>
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditAlbumModal;